import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useState } from "react";
import { useNotify, Confirm, useRecordContext } from "react-admin";
import { styled } from "@mui/material/styles";
import { useRewardMutation } from "../controller"; // Adjust the import path as necessary

const CustomDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const CustomDialogTitle = ({ children, onClose, ...other }: any) => {
  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

export const RewardCode = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const record = useRecordContext();

  const { generateReward } = useRewardMutation();
  const [numberValue, setNumberValue] = useState<number | null>(null);

  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const notify = useNotify();

  const handleClose = () => {
    setNumberValue(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (numberValue === null || isNaN(numberValue)) {
      notify("Please enter a valid number.", { type: "warning" });
      return;
    }

    setOpenConfirm(true);
  };

  const handleConfirmSubmit = async () => {
    setOpenConfirm(false);
    setIsLoading(true);

    if (!record || !record.id) {
      notify("No reward catalog selected.", { type: "error" });
      setIsLoading(false);
      return;
    }

    try {
      await generateReward.mutateAsync({
        input: {
          total_codes: numberValue,
          reward_catalog_id: record.id,
        },
      });

      notify("Reward codes generated successfully!", { type: "success" });
      handleClose();
    } catch (error: any) {
      notify(`Error generating reward codes: ${error.message}`, {
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <CustomDialog onClose={handleClose} open={open} fullWidth maxWidth="xs">
        <CustomDialogTitle onClose={handleClose}>
          Add Reward Codes
        </CustomDialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            Number of Codes to be generated:
          </Typography>
          <TextField
            type="number"
            fullWidth
            variant="outlined"
            value={numberValue ?? ""}
            onChange={(e) => setNumberValue(Number(e.target.value))}
            disabled={isLoading}
            placeholder="Enter a number"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={numberValue === null || isNaN(numberValue) || isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
          >
            {isLoading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </CustomDialog>

      <Confirm
        isOpen={openConfirm}
        loading={false}
        title="Generate Reward Codes"
        content={`Are you sure you want to generate ${numberValue} reward codes?`}
        onConfirm={handleConfirmSubmit}
        onClose={() => setOpenConfirm(false)}
        confirm="Generate"
        cancel="Cancel"
      />
    </>
  );
};
